import { Modal } from 'react-bootstrap';

import Locator from '../../../../shared/Locator';
import useDeleteDependency from '../../../hooks/api/dependencies/useDeleteDependency';
import useIgnoreDependency from '../../../hooks/api/dependencies/useIgnoreDependency';
import { nuc } from '../../../utils';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import { Link } from '../../generic/Link/index';
import DependencySummary from '../DependencySummary/DependencySummary';

import type { DependencyToRemove } from './types';

import styles from '../DependencySummary/DependencySummary.module.less';

type Props = {
  userRevisionLocator: string;
  dependencyToRemove: DependencyToRemove | null;
  setDependencyToRemove: (depToRemove: DependencyToRemove | null) => void;
  onRemoveDependency: (locator: string) => void;
};

/**
 * This modal lets users confirm ignoring (or, for manual dependencies, deleting) a dependency
 *
 * @param {object} props - ModalRemoveDependency properties
 * @param {string} props.userRevisionLocator - the revision the dependency is being ignored/deleted from
 * @param {DependencyToRemove | null} props.dependencyToRemove - dependency data; when null modal will not be displayed
 * @param {(depToRemove: DependencyToRemove | null) => void} props.setDependencyToRemove - callback to set dependencyToRemove
 * @param {(locator: string) => void} props.onRemoveDependency - callback for when dependency is removed (usually updates project)
 */
const ModalRemoveDependency = ({
  userRevisionLocator,
  dependencyToRemove,
  setDependencyToRemove,
  onRemoveDependency,
}: Props) => {
  const parentLocator = new Locator(userRevisionLocator);
  const parentProject = parentLocator.toPackageString();

  const { mutateAsync: ignoreDependency } = useIgnoreDependency(userRevisionLocator);
  const { mutateAsync: deleteDependency } = useDeleteDependency(userRevisionLocator);

  if (dependencyToRemove === null) {
    return null;
  }

  const shouldShow = dependencyToRemove !== null;

  const onHide = () => {
    setDependencyToRemove(null);
  };

  const onClickMethod = async () => {
    if (!dependencyToRemove?.locator) {
      return;
    }

    if (dependencyToRemove.isManual) {
      await deleteDependency(dependencyToRemove.locator);
    } else {
      await ignoreDependency(dependencyToRemove.locator);
    }

    onRemoveDependency(dependencyToRemove.locator);
    onHide();
  };

  const title = dependencyToRemove?.isManual ? 'Delete Dependency' : 'Ignore Dependency';
  const subtitle = dependencyToRemove?.isManual ? (
    <p>
      This dependency was added manually to this revision.
      It will be removed from this revision, and all future revisions.
    </p>
  ) : (
    <p>
      This dependency will be ignored for all past, present, and future revisions. This action
      can be undone in{' '}
      <Link
        className={styles.dependencySummarySubtitle}
        to={`/projects/${nuc(parentProject)}/settings`}>
        project settings
      </Link>
    </p>
  );

  return (
    <Modal
      dialogClassName={styles.dependencySummaryModalDialog}
      onHide={onHide}
      animation
      show={shouldShow}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
        <div className={styles.dependencySummarySubtitle}>
          {subtitle}
        </div>
      </Modal.Header>
      <div className={styles.dependencySummaryModalBody}>
        <Modal.Body>
          <DependencySummary summary={dependencyToRemove} />
        </Modal.Body>
      </div>
      <div className={styles.dependencySummaryModalFooter}>
        <Modal.Footer>
          <ButtonGroup>
            <Button
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onHide}>
              Cancel
            </Button>
            <Button
              type={BUTTON_TYPE.ERROR}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onClickMethod}>
              Confirm
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default ModalRemoveDependency;
